// Pagos: only closure records (restaurants/{id}/pagos, type === 'closure')

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../config/firebase/config';
import { useRestaurant } from '../../../providers/RestaurantContext';
import { signOut } from 'firebase/auth';
import { auth } from '../../../config/firebase/auth';
import { format } from 'date-fns';
import { es as localeEs } from 'date-fns/locale';
import { RefreshCw, Receipt, X, Filter, Clock, Home, LogOut } from 'lucide-react';
import { NotificationBell } from '../components/NotificationBell';
import Logo from '../../../assets/fonts/Solo-logo.png';

interface TransactionSummary {
  id: string;
  total?: number;
  approved?: boolean;
  processor?: string;
  paymentMethod?: string;
  createdAt?: any;
}

interface PagosEntry {
  id: string;
  type: string;
  mesaId: string;
  numeroMesa?: string;
  posOrderId?: string | null;
  montoTotal?: number;
  montoTotalConImpuestos?: number;
  montoPagado?: number;
  restante?: number;
  items?: Array<{ id?: string; name?: string; price?: number; quantity?: number; paid?: boolean }>;
  transactionIds?: string[];
  transactions?: TransactionSummary[];
  closedAt?: any;
  sortTime: number;
}

const formatCurrency = (n: number) =>
  new Intl.NumberFormat('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const PagosScreen: React.FC = () => {
  const navigate = useNavigate();
  const { restaurant } = useRestaurant();
  const [pagos, setPagos] = useState<PagosEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<PagosEntry | null>(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Filters
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [filterMesa, setFilterMesa] = useState<string>('');
  const [totalMode, setTotalMode] = useState<'above' | 'less'>('above');
  const [totalValue, setTotalValue] = useState<string>('');

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (e) {
      console.error('Logout failed', e);
    }
  };

  const loadPagos = React.useCallback(async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const pagosRef = collection(db, 'restaurants', restaurant.id, 'pagos');
      const snapshot = await getDocs(pagosRef);
      const toMillis = (t: any) => {
        if (!t) return 0;
        if (typeof t.toDate === 'function') return t.toDate().getTime();
        if (t.seconds != null) return t.seconds * 1000 + (t.nanoseconds ?? 0) / 1e6;
        return 0;
      };
      const entries: PagosEntry[] = snapshot.docs
        .filter((doc) => {
          const d = doc.data();
          return d.type === 'closure' || doc.id.startsWith('closure_');
        })
        .map((doc) => {
          const d = doc.data();
          const closedAt = d.closedAt;
          return {
            id: doc.id,
            type: 'closure',
            mesaId: d.mesaId ?? '',
            numeroMesa: d.numeroMesa,
            posOrderId: d.posOrderId ?? null,
            montoTotal: d.montoTotal,
            montoTotalConImpuestos: d.montoTotalConImpuestos,
            montoPagado: d.montoPagado,
            restante: d.restante,
            items: d.items ?? [],
            transactionIds: d.transactionIds,
            transactions: d.transactions ?? [],
            closedAt,
            sortTime: toMillis(closedAt),
          };
        });
      entries.sort((a, b) => b.sortTime - a.sortTime);
      setPagos(entries.slice(0, 100));
    } catch (err: any) {
      setError(err?.message ?? 'Error al cargar pagos.');
    } finally {
      setLoading(false);
    }
  }, [restaurant?.id]);

  useEffect(() => {
    loadPagos();
  }, [loadPagos]);

  const displayTotal = (p: PagosEntry) =>
    p.montoTotalConImpuestos ?? p.montoPagado ?? p.montoTotal ?? 0;

  const uniqueMesas = useMemo(() => {
    const set = new Set<string>();
    pagos.forEach((p) => {
      const label = p.numeroMesa ?? p.mesaId;
      if (label) set.add(label);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(b, undefined, { numeric: true }));
  }, [pagos]);

  const filteredPagos = useMemo(() => {
    let list = pagos;
    if (dateFrom) {
      const fromMs = new Date(dateFrom).setHours(0, 0, 0, 0);
      list = list.filter((p) => p.sortTime >= fromMs);
    }
    if (dateTo) {
      const toMs = new Date(dateTo).setHours(23, 59, 59, 999);
      list = list.filter((p) => p.sortTime <= toMs);
    }
    if (filterMesa.trim()) {
      const q = filterMesa.trim().toLowerCase();
      list = list.filter(
        (p) =>
          (p.numeroMesa ?? '').toLowerCase().includes(q) ||
          (p.mesaId ?? '').toLowerCase().includes(q)
      );
    }
    if (totalValue.trim()) {
      const num = parseFloat(totalValue.replace(/,/g, '.'));
      if (!Number.isNaN(num)) {
        if (totalMode === 'above') {
          list = list.filter((p) => displayTotal(p) >= num);
        } else {
          list = list.filter((p) => displayTotal(p) <= num);
        }
      }
    }
    return list;
  }, [pagos, dateFrom, dateTo, filterMesa, totalMode, totalValue]);

  const hasActiveFilters = dateFrom || dateTo || filterMesa.trim() || totalValue.trim();

  const clearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setFilterMesa('');
    setTotalValue('');
  };

  return (
    <div className="min-h-screen w-full flex flex-col px-6 py-6">
      {/* Header Bar — same as Mesas / Configuración */}
      <div className="flex items-center justify-between mb-8 w-full relative">
        <div className="flex items-center">
          <Link to="/admin" className="flex items-center gap-3 group">
            <img src={Logo} alt="YAP Logo" className="h-10 w-auto object-contain" />
            <span className="text-3xl font-bold tracking-tighter text-white group-hover:text-yap-primary transition-colors">
              yap
            </span>
          </Link>
        </div>
        <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 bg-zinc-900/40 backdrop-blur-xl border border-white/5 rounded-full">
          <Clock size={16} className="text-zinc-400" />
          <div className="flex flex-col">
            <span className="text-sm font-bold text-white leading-none">
              {currentTime.toLocaleTimeString('es-DO', { hour: '2-digit', minute: '2-digit' })}
            </span>
            <span className="text-[10px] text-zinc-500 font-medium leading-none mt-0.5">
              {currentTime.toLocaleDateString('es-DO', { weekday: 'short', day: 'numeric', month: 'short' })}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <Link
            to="/admin"
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors font-medium"
          >
            <Home size={18} />
            <span>Volver a inicio</span>
          </Link>
          <div className="w-px h-6 bg-zinc-700" />
          <NotificationBell />
          <div className="w-px h-6 bg-zinc-700" />
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 text-sm text-zinc-400 hover:text-red-500 transition-colors font-medium"
          >
            <LogOut size={18} />
            <span>Cerrar sesión</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-col md:flex-row max-w-7xl mx-auto w-full gap-6">
      {/* Sidebar - Filtros */}
      <aside className="w-full md:w-72 shrink-0">
        <div className="rounded-2xl bg-zinc-900/40 backdrop-blur-sm border border-white/5 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-yap-primary/10">
              <Filter size={16} className="text-yap-primary" />
            </div>
            <span className="text-sm font-semibold text-white">Filtros</span>
          </div>
          <div className="p-4 space-y-5">
            <div className="space-y-3">
              <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Fecha</p>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="sr-only">Desde</label>
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-sm text-white placeholder-zinc-500 focus:border-yap-primary/50 focus:ring-1 focus:ring-yap-primary/30 [color-scheme:dark]"
                  />
                </div>
                <div>
                  <label className="sr-only">Hasta</label>
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full rounded-lg border border-white/5 bg-white/5 px-3 py-2 text-sm text-white placeholder-zinc-500 focus:border-yap-primary/50 focus:ring-1 focus:ring-yap-primary/30 [color-scheme:dark]"
                  />
                </div>
              </div>
            </div>
            <div className="h-px bg-white/5" />
            <div className="space-y-2">
              <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Mesa</p>
              <select
                value={filterMesa}
                onChange={(e) => setFilterMesa(e.target.value)}
                className="w-full rounded-lg border border-white/5 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-yap-primary/50 focus:ring-1 focus:ring-yap-primary/30 [color-scheme:dark]"
              >
                <option value="">Todas las mesas</option>
                {uniqueMesas.map((m) => (
                  <option key={m} value={m}>
                    Mesa {m}
                  </option>
                ))}
              </select>
            </div>
            <div className="h-px bg-white/5" />
            <div className="space-y-2">
              <p className="text-[11px] font-medium uppercase tracking-wider text-zinc-500">Total RD$</p>
              <div className="flex gap-2">
                <select
                  value={totalMode}
                  onChange={(e) => setTotalMode(e.target.value as 'above' | 'less')}
                  className="shrink-0 w-[130px] rounded-lg border border-white/5 bg-white/5 px-3 py-2.5 text-sm text-white focus:border-yap-primary/50 focus:ring-1 focus:ring-yap-primary/30 [color-scheme:dark]"
                >
                  <option value="above">≥ Mayor o igual</option>
                  <option value="less">≤ Menor o igual</option>
                </select>
                <input
                  type="number"
                  placeholder="Ej. 1000"
                  min={0}
                  step={0.01}
                  value={totalValue}
                  onChange={(e) => setTotalValue(e.target.value)}
                  className="flex-1 min-w-0 rounded-lg border border-white/5 bg-white/5 px-3 py-2.5 text-sm text-white placeholder-zinc-500 focus:border-yap-primary/50 focus:ring-1 focus:ring-yap-primary/30"
                />
              </div>
            </div>
            {hasActiveFilters && (
              <>
                <div className="h-px bg-white/5" />
                <button
                  type="button"
                  onClick={clearFilters}
                  className="w-full py-2.5 text-sm font-medium text-zinc-400 hover:text-white rounded-lg border border-white/10 hover:border-yap-primary/30 hover:bg-yap-primary/5 transition-colors"
                >
                  Limpiar filtros
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Content area */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight mb-1">
              Pagos y cierres
            </h1>
            <p className="text-zinc-400 text-base">
              {filteredPagos.length === pagos.length
                ? 'Cierres de mesa'
                : `${filteredPagos.length} de ${pagos.length} cierres`}
            </p>
          </div>
          <button
            onClick={loadPagos}
            disabled={loading}
            className="p-2 rounded-lg bg-zinc-800/60 text-zinc-400 hover:text-white disabled:opacity-50"
            title="Actualizar"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Cargando cierres...</span>
        </div>
      ) : error ? (
        <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-4">
          <p className="text-red-400 font-medium">Error al cargar pagos</p>
          <p className="text-red-300/80 text-sm mt-1">{error}</p>
        </div>
      ) : filteredPagos.length === 0 ? (
        <div className="rounded-xl bg-zinc-900/40 border border-white/5 p-8 text-center">
          <Receipt className="w-12 h-12 mx-auto text-zinc-500 mb-3" />
          <p className="text-zinc-400">
            {hasActiveFilters ? 'Ningún cierre coincide con los filtros.' : 'No hay cierres de mesa aún.'}
          </p>
          <p className="text-zinc-500 text-sm mt-1">
            {hasActiveFilters ? 'Prueba otros filtros o limpia.' : 'Los cierres aparecerán aquí.'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPagos.map((p) => (
            <button
              type="button"
              key={p.id}
              onClick={() => setSelected(p)}
              className="w-full text-left rounded-xl bg-zinc-900/40 border border-white/5 p-4 flex flex-wrap items-center justify-between gap-4 hover:bg-zinc-800/60 hover:border-yap-primary/20 transition-colors"
            >
              <div className="flex items-center gap-4">
                <span className="text-xs font-medium px-2 py-1 rounded bg-zinc-800 text-zinc-400">
                  Cierre
                </span>
                <span className="font-semibold text-white">Mesa {p.numeroMesa ?? p.mesaId}</span>
                {p.posOrderId && (
                  <span className="text-xs text-zinc-500 font-mono truncate max-w-[180px]">
                    {p.posOrderId}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-6 text-sm">
                <span className="text-zinc-400">
                  Total: <span className="text-white font-medium">RD$ {formatCurrency(displayTotal(p))}</span>
                </span>
                {p.montoPagado != null && (
                  <span className="text-green-400">Pagado: RD$ {formatCurrency(p.montoPagado)}</span>
                )}
                {(p.closedAt?.toDate || p.sortTime) && (
                  <span className="text-zinc-500">
                    {p.closedAt?.toDate
                      ? format(p.closedAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: localeEs })
                      : format(new Date(p.sortTime), 'dd/MM/yyyy HH:mm', { locale: localeEs })}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-zinc-900 border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">
                Cierre – Mesa {selected.numeroMesa ?? selected.mesaId}
              </h2>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 overflow-y-auto space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="text-zinc-500">Total con impuesto</div>
                <div className="text-white font-medium">RD$ {formatCurrency(displayTotal(selected))}</div>
                <div className="text-zinc-500">Pagado</div>
                <div className="text-green-400">RD$ {formatCurrency(selected.montoPagado ?? 0)}</div>
                <div className="text-zinc-500">Restante</div>
                <div className="text-white">RD$ {formatCurrency(selected.restante ?? 0)}</div>
                {selected.posOrderId && (
                  <>
                    <div className="text-zinc-500">Ticket / Orden</div>
                    <div className="text-zinc-400 font-mono text-xs break-all">{selected.posOrderId}</div>
                  </>
                )}
                {selected.closedAt?.toDate && (
                  <>
                    <div className="text-zinc-500">Cerrado</div>
                    <div className="text-zinc-400">
                      {format(selected.closedAt.toDate(), 'dd/MM/yyyy HH:mm', { locale: localeEs })}
                    </div>
                  </>
                )}
              </div>

              {selected.items && selected.items.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-400 mb-2">Ítems</h3>
                  <ul className="space-y-2">
                    {selected.items.map((item, i) => (
                      <li
                        key={item.id ?? i}
                        className="flex justify-between text-sm py-2 border-b border-white/5 last:border-0"
                      >
                        <span className="text-white">
                          {item.name ?? 'Ítem'} × {item.quantity ?? 1}
                        </span>
                        <span className="text-zinc-400">
                          RD$ {formatCurrency((item.price ?? 0) * (item.quantity ?? 1))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selected.transactions && selected.transactions.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-zinc-400 mb-2">Transacciones</h3>
                  <ul className="space-y-2">
                    {selected.transactions.map((tx) => (
                      <li
                        key={tx.id}
                        className="flex justify-between items-center text-sm py-2 border-b border-white/5 last:border-0"
                      >
                        <div>
                          <span className="text-white font-medium">{tx.paymentMethod ?? tx.processor ?? tx.id}</span>
                          {tx.approved != null && (
                            <span className={tx.approved ? 'text-green-400 ml-2' : 'text-red-400'}>
                              {tx.approved ? 'Aprobada' : 'Declinada'}
                            </span>
                          )}
                        </div>
                        {tx.total != null && (
                          <span className="text-zinc-400">RD$ {formatCurrency(tx.total)}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
      </div>
    </div>
  );
};

export default PagosScreen;
